import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Edit, Trash2, Search, MapPin, X } from 'lucide-react';
import { placeTypesAPI } from '../api';
import { PlaceType, GoogleType, CreatePlaceTypeRequest } from '../types';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/common/PageHeader';
import { LoadingState } from '../components/common/LoadingState';
import { ErrorState } from '../components/common/ErrorState';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { useNotification } from '../hooks/useNotification';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';

export const PlaceTypesPage: React.FC = () => {
  const [placeTypes, setPlaceTypes] = useState<PlaceType[]>([]);
  const [googleTypes, setGoogleTypes] = useState<GoogleType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedPlaceType, setSelectedPlaceType] = useState<PlaceType | null>(null);
  const [formData, setFormData] = useState<CreatePlaceTypeRequest>({
    name: '',
    description: '',
    icon: '',
    color: '#007bff',
    google_types: [],
    is_active: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showNotification } = useNotification();

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [placeTypesData, googleTypesData] = await Promise.all([
        placeTypesAPI.getAllPlaceTypes({
          search: searchTerm || undefined,
          is_active: showInactive ? undefined : true,
        }),
        placeTypesAPI.getGoogleTypes(),
      ]);
      setPlaceTypes(Array.isArray(placeTypesData) ? placeTypesData : []);
      setGoogleTypes(Array.isArray(googleTypesData) ? googleTypesData : []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, showInactive]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter place types
  const filteredPlaceTypes = useMemo(() => {
    if (!Array.isArray(placeTypes)) return [];
    return placeTypes.filter(placeType => {
      if (!showInactive && !placeType.is_active) return false;
      if (searchTerm) {
        return placeType.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
               placeType.description?.toLowerCase().includes(searchTerm.toLowerCase());
      }
      return true;
    });
  }, [placeTypes, searchTerm, showInactive]);

  // Available icons for map filters
  const availableIcons = [
    { value: 'active', label: 'Be Active' },
    { value: 'restaurant', label: 'Restaurant' },
    { value: 'learn', label: 'Learn' },
    { value: 'chill', label: 'Chill' },
    { value: 'fitness', label: 'Exercise' },
    { value: 'go-out', label: 'Go Out' },
    { value: 'play-games', label: 'Play Games' },
    { value: 'create', label: 'Create' },
    { value: 'turn-up', label: 'Turn Up' },
    { value: 'filter', label: 'Default' },
  ];

  // Handle create/edit dialog
  const handleOpenCreateDialog = () => {
    setFormData({
      name: '',
      description: '',
      icon: 'filter',
      color: '#007bff',
      google_types: [],
      is_active: true,
    });
    setIsCreateDialogOpen(true);
  };

  const handleOpenEditDialog = (placeType: PlaceType) => {
    setSelectedPlaceType(placeType);
    setFormData({
      name: placeType.name,
      description: placeType.description || '',
      icon: placeType.icon || 'filter',
      color: placeType.color || '#007bff',
      google_types: placeType.google_types,
      is_active: placeType.is_active,
    });
    setIsEditDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      if (selectedPlaceType) {
        await placeTypesAPI.updatePlaceType(selectedPlaceType._id, formData);
        showNotification('Map filter updated successfully', 'success');
      } else {
        await placeTypesAPI.createPlaceType(formData);
        showNotification('Map filter created successfully', 'success');
      }
      await fetchData();
      setIsCreateDialogOpen(false);
      setIsEditDialogOpen(false);
      setSelectedPlaceType(null);
    } catch (err) {
      showNotification('Failed to save map filter', 'error');
      console.error('Error saving map filter:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = (placeType: PlaceType) => {
    setSelectedPlaceType(placeType);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedPlaceType) return;
    
    try {
      setIsSubmitting(true);
      await placeTypesAPI.deletePlaceType(selectedPlaceType._id);
      showNotification('Map filter deleted successfully', 'success');
      await fetchData();
      setIsDeleteDialogOpen(false);
      setSelectedPlaceType(null);
    } catch (err) {
      showNotification('Failed to delete map filter', 'error');
      console.error('Error deleting map filter:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Google type toggle
  const handleGoogleTypeToggle = (googleType: string) => {
    setFormData(prev => ({
      ...prev,
      google_types: prev.google_types.includes(googleType)
        ? prev.google_types.filter(type => type !== googleType)
        : [...prev.google_types, googleType]
    }));
  };

  if (loading) return <DashboardLayout><LoadingState /></DashboardLayout>;
  if (error) return <DashboardLayout><ErrorState message={error} /></DashboardLayout>;

  return (
    <DashboardLayout>
      <PageHeader
        title="Map Filters"
        description="Configure location filters for map display"
      />

      <div className="space-y-6">
        {/* Actions Bar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex-1 max-w-md flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search map filters..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="show-inactive"
                    checked={showInactive}
                    onCheckedChange={setShowInactive}
                  />
                  <Label htmlFor="show-inactive" className="text-sm">
                    Show inactive
                  </Label>
                </div>
              </div>
              <Button onClick={handleOpenCreateDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Add Map Filter
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Place Types Grid */}
        {filteredPlaceTypes.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No map filters found</h3>
              <p className="text-gray-500 mb-4">
                {searchTerm
                  ? "No map filters found matching your search."
                  : "Create your first map filter to start configuring location filters."}
              </p>
              {!searchTerm && (
                <Button onClick={handleOpenCreateDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Map Filter
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredPlaceTypes.map((placeType) => (
              <Card key={placeType._id}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg">{placeType.name}</h3>
                        {placeType.icon && (
                          <Badge variant="outline" className="text-xs">
                            {placeType.icon}
                          </Badge>
                        )}
                        {placeType.color && (
                          <div 
                            className="w-5 h-5 rounded border border-gray-300" 
                            style={{ backgroundColor: placeType.color }}
                            title={placeType.color}
                          />
                        )}
                      </div>
                      {placeType.description && (
                        <p className="text-sm text-gray-500">{placeType.description}</p>
                      )}
                    </div>
                    <Badge variant={placeType.is_active ? "default" : "secondary"}>
                      {placeType.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  
                  <div className="mb-3">
                    <p className="text-sm font-medium mb-2">Google Types ({placeType.google_types?.length || 0})</p>
                    <div className="flex flex-wrap gap-1">
                      {placeType.google_types?.slice(0, 3).map((type) => (
                        <Badge key={type} variant="outline" className="text-xs">
                          {type.replace(/_/g, ' ')}
                        </Badge>
                      )) || []}
                      {(placeType.google_types?.length || 0) > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{(placeType.google_types?.length || 0) - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>

                  {placeType.places_count !== undefined && (
                    <p className="text-sm text-gray-500 mb-3">
                      <strong>{placeType.places_count}</strong> places
                    </p>
                  )}

                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenEditDialog(placeType)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(placeType)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Map Filter</DialogTitle>
            <DialogDescription>
              Add a new map filter to categorize locations
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Food & Dining"
                required
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this map filter category..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="icon">Icon</Label>
                <select
                  id="icon"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {availableIcons.map((icon) => (
                    <option key={icon.value} value={icon.value}>
                      {icon.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="color">Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="color"
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-20 h-10"
                  />
                  <Input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    placeholder="#007bff"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked: boolean) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
            <div>
              <Label className="mb-2 block">
                Select Google Place Types
                {formData.google_types.length > 0 && (
                  <Badge className="ml-2">{formData.google_types.length} selected</Badge>
                )}
              </Label>
              <p className="text-sm text-gray-500 mb-3">
                Choose which Google place types should be included in this category
              </p>
              
              <div className="border rounded-lg max-h-64 overflow-y-auto">
                {googleTypes.map((googleType) => (
                  <div
                    key={googleType.type}
                    className={`flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer ${
                      formData.google_types.includes(googleType.type) ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => handleGoogleTypeToggle(googleType.type)}
                  >
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        checked={formData.google_types.includes(googleType.type)}
                        onCheckedChange={() => handleGoogleTypeToggle(googleType.type)}
                      />
                      <span className="text-sm font-medium">
                        {googleType.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {googleType.places_count} places
                    </Badge>
                  </div>
                ))}
              </div>

              {formData.google_types.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm text-gray-500 mb-2">Selected types:</p>
                  <div className="flex flex-wrap gap-1">
                    {formData.google_types.map((type) => (
                      <Badge
                        key={type}
                        variant="default"
                        className="cursor-pointer"
                        onClick={() => handleGoogleTypeToggle(type)}
                      >
                        {type.replace(/_/g, ' ')}
                        <X className="ml-1 h-3 w-3" />
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={isSubmitting || !formData.name || formData.google_types.length === 0}
            >
              {isSubmitting ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Map Filter</DialogTitle>
            <DialogDescription>
              Update the map filter details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Food & Dining"
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this map filter category..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-icon">Icon</Label>
                <select
                  id="edit-icon"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {availableIcons.map((icon) => (
                    <option key={icon.value} value={icon.value}>
                      {icon.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="edit-color">Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="edit-color"
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-20 h-10"
                  />
                  <Input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    placeholder="#007bff"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="edit-is_active"
                checked={formData.is_active}
                onCheckedChange={(checked: boolean) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="edit-is_active">Active</Label>
            </div>
            <div>
              <Label className="mb-2 block">
                Select Google Place Types
                {formData.google_types.length > 0 && (
                  <Badge className="ml-2">{formData.google_types.length} selected</Badge>
                )}
              </Label>
              <p className="text-sm text-gray-500 mb-3">
                Choose which Google place types should be included in this category
              </p>
              
              <div className="border rounded-lg max-h-64 overflow-y-auto">
                {googleTypes.map((googleType) => (
                  <div
                    key={googleType.type}
                    className={`flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer ${
                      formData.google_types.includes(googleType.type) ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => handleGoogleTypeToggle(googleType.type)}
                  >
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        checked={formData.google_types.includes(googleType.type)}
                        onCheckedChange={() => handleGoogleTypeToggle(googleType.type)}
                      />
                      <span className="text-sm font-medium">
                        {googleType.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {googleType.places_count} places
                    </Badge>
                  </div>
                ))}
              </div>

              {formData.google_types.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm text-gray-500 mb-2">Selected types:</p>
                  <div className="flex flex-wrap gap-1">
                    {formData.google_types.map((type) => (
                      <Badge
                        key={type}
                        variant="default"
                        className="cursor-pointer"
                        onClick={() => handleGoogleTypeToggle(type)}
                      >
                        {type.replace(/_/g, ' ')}
                        <X className="ml-1 h-3 w-3" />
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={isSubmitting || !formData.name || formData.google_types.length === 0}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the map filter "{selectedPlaceType?.name}"?
              {selectedPlaceType?.places_count && selectedPlaceType.places_count > 0 && (
                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800">
                    ⚠️ This map filter is currently used by {selectedPlaceType.places_count} places.
                  </p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={isSubmitting}>
              {isSubmitting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};